import React, { useCallback, useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { FormHelperText, Grid } from '@material-ui/core';
import { useFormContext } from 'react-hook-form';
import { selectProject } from '../../../../store/modules/projects/selectors';
import { ReactFormInput, SceneSelect } from '../../../../components';
import StepModalCaption from '../../../../components/modal/StepModalCaption/StepModalCaption';
import { Option } from '../../../../components/form-simple-autocomplete/FormSimpleAutoComplete';
import useStyles from './styles';
import { closedCaptions } from './constants';
import ReactFormCheckbox from '../../../../components/form-checkbox/ReactFormCheckbox';
import ReactFormRadioGroup from '../../../../components/form-radio-group/ReactFormRadioGroup';

type Props = {
  setModalTitle: (title: string) => void;
};

const StepOne = ({ setModalTitle }: Props) => {
  const classes = useStyles();
  const { data: project } = useSelector(selectProject);
  const { watch, errors, setValue, setError } = useFormContext();
  const sceneTitle = watch('options.scene_title');
  const checkboxStandard = watch('options.format.standard');
  const checkboxSquare = watch('options.format.square');
  const checkboxPortrait = watch('options.format.portrait');
  const sceneId = watch('scene_id');

  const savedSceneOption = useMemo<Option | undefined>(() => {
    if (sceneId !== -1) {
      return { id: sceneId, label: sceneTitle };
    }

    return undefined;
  }, [sceneId, sceneTitle]);

  const onChangeScript = useCallback((option?: Option) => {
    if (option) {
      setValue('options.scene_title', option.label);
    } else {
      setValue('options.scene_title', '');
    }
  }, [setValue]);

  useEffect(() => {
    setModalTitle('New Video Edit Order');
  }, [setModalTitle]);

  useEffect(() => {
    setValue('options.format.standard', checkboxStandard, { shouldValidate: true });
  }, [checkboxSquare, checkboxPortrait, checkboxStandard, setValue]);

  // set script id required for blocking next button on start
  /* eslint-disable react-hooks/exhaustive-deps */
  useEffect(() => {
    if (sceneId === -1) {
      setError('scene_id', { message: 'Script is required', type: 'required' });
    }
  }, [setError]);

  return (
    <Grid container spacing={4}>

      <Grid item xs={12} lg={8}>

        <div className={classes.inputWrapper}>
          <SceneSelect
            name="scene_id"
            label="Script"
            filters={[{ key: 'project_id', value: project!.id }]}
            callback={onChangeScript}
            isRequired
            formType="react"
            setOption={savedSceneOption}
          />
        </div>

        <ReactFormInput
          name="options.order_title"
          label="Order Title (optional)"
        />

        <StepModalCaption
          title="Format"
          tooltip="Select the video formats that you want to use for different web site and social media services"
        />

        <ReactFormCheckbox
          label="Standard: YouTube / Facebook / Vimeo / LinkedIn"
          name="options.format.standard"
        />
        <ReactFormCheckbox
          label="Square: Instagram"
          name="options.format.square"
        />
        <ReactFormCheckbox
          label="Portrait: Instagram Story, IGTV, Facebook Story"
          name="options.format.portrait"
        />

        {errors?.options?.format?.standard && (
          <FormHelperText id="Format" error>{errors.options.format.standard.message}</FormHelperText>
        )}

        <StepModalCaption
          title="Closed Captions / Subtitles in U.S. English"
          tooltip="Subtitles can be included in the video file (most common method) or in a separate file."
        />

        <ReactFormRadioGroup name="options.closed_captions" options={closedCaptions} />

      </Grid>

    </Grid>

  );
};

export default StepOne;
